import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { SITE_URL, SITE_NAME, FAVICON_URL } from '@/lib/seo';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  const articleJsonLd = article
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.subtitle,
        image: article.heroImage,
        datePublished: '2026-08-26',
        dateModified: '2026-08-26',
        author: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: `${SITE_URL}/`,
        },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: {
            '@type': 'ImageObject',
            url: FAVICON_URL,
          },
        },
        mainEntityOfPage: `${SITE_URL}/articles/${article.slug}`,
      }
    : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-heading text-3xl font-semibold text-foreground-900 mb-4">
            Article Not Found
          </h1>
          <p className="text-foreground-500 mb-8">
            The article you are looking for does not exist or has been moved.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Back to Homepage
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd} />
      <Navbar />

      {/* Article Hero */}
      <section className="relative h-[380px] md:h-[480px] overflow-hidden">
        <img src={article.heroImage} alt={article.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/60 via-primary-950/70 to-primary-950/90" />
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <nav className="text-sm text-background-300 mb-4 flex flex-wrap items-center gap-2">
                <Link to="/" className="hover:text-background-50 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-background-50">{article.category}</span>
              </nav>
              <h1 className="font-heading text-3xl md:text-5xl font-semibold text-background-50 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-background-200 max-w-2xl">
                {article.subtitle}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-background-300">
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}</span>
                <span>·</span>
                <span className="bg-accent-500/20 text-accent-300 px-3 py-1 rounded-full">{article.category}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-20">
        <div className="max-w-4xl mx-auto">
          {/* Table of Contents */}
          <div className="bg-background-100 rounded-xl p-6 md:p-8 mb-12 border border-background-200/70">
            <h2 className="font-heading text-lg font-semibold text-foreground-800 mb-4">On This Page</h2>
            <ul className="space-y-2">
              {article.sections.map((section, i) => (
                <li key={i}>
                  <a href={`#section-${i + 1}`} className="text-sm text-foreground-600 hover:text-accent-600 transition-colors flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary-50 text-primary-600 text-xs flex items-center justify-center font-medium shrink-0">{i + 1}</span>
                    {section.heading}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Sections */}
          {article.sections.map((section, i) => (
            <section key={i} id={`section-${i + 1}`} className="mb-12 md:mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
                {section.heading}
              </h2>
              {section.paragraphs.map((p, j) => (
                <p key={j} className="text-base md:text-lg text-foreground-600 leading-relaxed mb-5">
                  {p}
                </p>
              ))}
              {section.items && (
                <ul className="space-y-3 mb-6">
                  {section.items.map((item, k) => (
                    <li key={k} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />
                      <span className="text-sm md:text-base text-foreground-600">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {section.table && (
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm border border-background-200/70 rounded-xl overflow-hidden">
                    <thead>
                      <tr className="bg-primary-50">
                        {section.table.headers.map((h, k) => (
                          <th key={k} className="text-left px-4 py-3 font-medium text-foreground-700">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200/70">
                      {section.table.rows.map((row, r) => (
                        <tr key={r} className="bg-background-50 hover:bg-background-100 transition-colors">
                          {row.map((cell, c) => (
                            <td key={c} className={`px-4 py-3 ${c === 0 ? 'font-medium text-foreground-800' : 'text-foreground-600'}`}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {article.faqs.map((faq, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70">
                  <h3 className="font-medium text-foreground-800 mb-2">Q: {faq.q}</h3>
                  <p className="text-sm md:text-base text-foreground-600">A: {faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal Linking */}
          <section className="bg-accent-50/30 border border-accent-200/50 rounded-xl p-6 md:p-8 mb-12">
            <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-4">Related Resources</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Link to="/" className="flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors">
                <i className="ri-arrow-right-line text-accent-500" />
                Back to AllChinaBuy Spreadsheet Hub
              </Link>
              {articles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((a) => (
                  <Link key={a.slug} to={`/articles/${a.slug}`} className="flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors">
                    <i className="ri-arrow-right-line text-accent-500" />
                    {a.title}
                  </Link>
                ))}
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 text-sm text-accent-700 hover:text-accent-800 transition-colors">
                <i className="ri-external-link-line text-accent-500" />
                Visit Main Website — Kaspreadsheet
              </a>
            </div>
          </section>

          {/* Final CTA */}
          <div className="text-center bg-primary-950 rounded-xl p-8 md:p-12">
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-4">
              Ready to Start Using AllChinaBuy?
            </h3>
            <p className="text-sm md:text-base text-background-300 mb-6 max-w-xl mx-auto">
              Explore our curated spreadsheet resources, find verified products, and shop with confidence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-accent-500 text-background-50 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors inline-flex items-center gap-2"
              >
                {t('final_cta_buy_now')}
                <i className="ri-external-link-line" />
              </a>
              <Link
                to="/"
                className="px-6 py-3 bg-background-50/10 backdrop-blur text-background-50 border border-background-200/30 rounded-lg font-medium text-sm hover:bg-background-50/20 transition-colors inline-flex items-center gap-2"
              >
                Back to Homepage
                <i className="ri-arrow-left-line" />
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}